import { useCallback, useEffect, useState } from 'react';
import { useWebSocket } from './useWebSocket';

interface IrisStatus {
  state: 'open' | 'closed' | 'unknown';
  action?: 'open' | 'close';
  success?: boolean;
  error?: string;
  timestamp: number;
}

interface IrisControlHook {
  openIris: (duration?: number) => void;
  closeIris: () => void;
  getIrisStatus: () => void;
  irisState: 'open' | 'closed' | 'unknown';
  isConnected: boolean;
}

export function useIrisControl(): IrisControlHook {
  const { sendMessage, isConnected } = useWebSocket();
  const [irisState, setIrisState] = useState<'open' | 'closed' | 'unknown'>('unknown');

  // Debug: Log hook initialization
  useEffect(() => {
    console.log('🔧 [useIrisControl] Hook initialized', {
      isConnected,
      irisState,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Debug: Log connection state changes
  useEffect(() => {
    console.log('🔌 [useIrisControl] Connection state changed:', {
      isConnected,
      timestamp: new Date().toISOString()
    });
  }, [isConnected]);

  // Handle iris status messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'iris_status') {
          const status = message as IrisStatus;
          
          // Update iris state
          if (status.state) {
            setIrisState(status.state);
          }
          
          // Log status for debugging
          console.log('📬 [useIrisControl] Iris status received:', {
            state: status.state,
            previousState: irisState,
            action: status.action,
            success: status.success,
            error: status.error,
            timestamp: new Date().toISOString()
          });
          
          // Handle errors
          if (status.error) {
            console.error('❌ Iris control error:', status.error);
          }
        }
      } catch (error) {
        console.error('Error parsing iris message:', error);
      }
    };

    // Get WebSocket instance from window
    const ws = (window as any).webSocketInstance;
    if (ws) {
      ws.addEventListener('message', handleMessage);
      
      // Query initial status when connected
      if (isConnected) {
        sendMessage({ type: 'iris_status' });
      }
      
      return () => {
        ws.removeEventListener('message', handleMessage);
      };
    }
  }, [isConnected, sendMessage]);

  // Open iris with optional duration
  const openIris = useCallback((duration?: number) => {
    const callStack = new Error().stack?.split('\n')[2]?.trim();
    console.log('🟢 [openIris] Called from:', callStack);
    
    if (!isConnected) {
      console.warn('⚠️ [openIris] Cannot open iris: WebSocket not connected', {
        isConnected,
        timestamp: new Date().toISOString(),
        calledFrom: callStack
      });
      return;
    }
    
    console.log(`🔵 [openIris] Opening iris${duration ? ` for ${duration} seconds` : ''}...`, {
      duration,
      isConnected,
      currentState: irisState,
      timestamp: new Date().toISOString()
    });
    
    sendMessage({
      type: 'iris_open',
      duration: duration
    });
  }, [isConnected, sendMessage, irisState]);

  // Close iris
  const closeIris = useCallback(() => {
    const callStack = new Error().stack?.split('\n')[2]?.trim();
    console.log('🔴 [closeIris] Called from:', callStack);
    
    if (!isConnected) {
      console.warn('⚠️ [closeIris] Cannot close iris: WebSocket not connected', {
        isConnected,
        timestamp: new Date().toISOString(),
        calledFrom: callStack
      });
      return;
    }
    
    console.log('🔵 [closeIris] Closing iris...', {
      isConnected,
      currentState: irisState,
      timestamp: new Date().toISOString()
    });
    
    sendMessage({
      type: 'iris_close'
    });
  }, [isConnected, sendMessage, irisState]);

  // Get current iris status
  const getIrisStatus = useCallback(() => {
    if (!isConnected) {
      console.warn('⚠️ Cannot get iris status: WebSocket not connected');
      return;
    }
    
    sendMessage({
      type: 'iris_status'
    });
  }, [isConnected, sendMessage]);

  return {
    openIris,
    closeIris,
    getIrisStatus,
    irisState,
    isConnected
  };
}

// Export convenience functions for direct use
export const irisControl = {
  open: (duration?: number) => {
    const ws = (window as any).webSocketInstance;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'iris_open',
        duration: duration
      }));
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  },
  
  close: () => {
    const ws = (window as any).webSocketInstance;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'iris_close'
      }));
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  },
  
  status: () => {
    const ws = (window as any).webSocketInstance;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'iris_status'
      }));
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  }
};
